import { SITE_URL, OG_IMAGE_URL, INSTAGRAM_URL } from '../funnel/config';

// SEO por ruta (hash). Las páginas internas nunca se indexan.

const BRAND_COLOR = '#9B1B42';
const SITE_NAME = 'Chale Rodriguez';
const TITLE = 'Chale Rodriguez | Construye tu propia cartera de clientes en finanzas';
const DESCRIPTION =
  'Llamada 1:1 con Chale: el sistema paso a paso para construir tu cartera de clientes en finanzas, sin experiencia y con prospectos que llegan por anuncios.';

type RouteSeo = { title: string; robots: string; canonical: boolean };

const ROUTES: Record<string, RouteSeo> = {
  '/': { title: TITLE, robots: 'index, follow', canonical: true },
  '/aplicar': { title: `Tu aplicación | ${SITE_NAME}`, robots: 'noindex, nofollow', canonical: false },
  '/agenda': { title: `Elige tu horario | ${SITE_NAME}`, robots: 'noindex, nofollow', canonical: false },
  '/confirmada': { title: `Llamada confirmada | ${SITE_NAME}`, robots: 'noindex, nofollow', canonical: false },
};

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setJsonLd() {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: SITE_NAME,
    jobTitle: 'Representante independiente de servicios financieros',
    address: { '@type': 'PostalAddress', addressLocality: 'Miami', addressRegion: 'FL', addressCountry: 'US' },
  };
  if (SITE_URL) data.url = SITE_URL;
  if (OG_IMAGE_URL) data.image = OG_IMAGE_URL;
  if (INSTAGRAM_URL) data.sameAs = [INSTAGRAM_URL];

  let el = document.getElementById('jsonld-person') as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'jsonld-person';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function preconnect(href: string, crossOrigin?: boolean) {
  if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'preconnect';
  l.href = href;
  if (crossOrigin) l.crossOrigin = 'anonymous';
  document.head.appendChild(l);
}

let initialized = false;

function initSeo() {
  if (initialized) return;
  initialized = true;
  document.documentElement.lang = 'es';
  preconnect('https://fonts.googleapis.com');
  preconnect('https://fonts.gstatic.com', true);

  setMeta('name', 'theme-color', BRAND_COLOR);
  setMeta('name', 'description', DESCRIPTION);

  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:locale', 'es_US');
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:title', TITLE);
  setMeta('property', 'og:description', DESCRIPTION);
  setMeta('property', 'og:url', SITE_URL);
  setMeta('property', 'og:image', OG_IMAGE_URL);
  setMeta('property', 'og:image:width', OG_IMAGE_URL ? '1200' : '');
  setMeta('property', 'og:image:height', OG_IMAGE_URL ? '630' : '');

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', TITLE);
  setMeta('name', 'twitter:description', DESCRIPTION);
  setMeta('name', 'twitter:image', OG_IMAGE_URL);

  setJsonLd();
}

// En cada cambio de ruta: título, robots y canonical
export function applySeo(route: string) {
  initSeo();
  const seo = ROUTES[route] || ROUTES['/'];
  document.title = seo.title;
  setMeta('name', 'robots', seo.robots);
  setLink('canonical', seo.canonical ? SITE_URL : '');
}
